import { Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { catchError, Observable } from 'rxjs';

import { BasedataService } from '../shared/basedata.service';

import { Order, Orders } from './order';
import { ConfigService } from '@docma-mm-core/config/config.service';

const savedProjectOrdersKey = 'mm-savedProjectOrders';

@Injectable()
export class OrderService extends BasedataService {

  private projectUrl = '/projects/';
  private orderUrl = '/orders';
  private orderConfigurationUrl = '/configuration/orders';

  constructor(private http: HttpClient, configService: ConfigService) {
    super(configService);
  }

  private getOrderUrl() {
    return super.getFullUrl(this.orderUrl);
  }

  private getProjectOrderUrl(projectId: number) {
    return super.getFullUrl(this.projectUrl + projectId + this.orderUrl);
  }

  private getProjectOrderConfigurationUrl(projectId: number) {
    return super.getFullUrl(this.projectUrl + projectId + this.orderConfigurationUrl);
  }

  // get orders list
  getOrders(top: number = 10000, skip: number = 0): Observable<Orders> {
    const endpoint = this.getOrderUrl();
    let httpParams = new HttpParams();
    if (top) {
      httpParams = httpParams.set('$top', top.toString());
    }
    if (skip) {
      httpParams = httpParams.set('$skip', skip.toString());
    }
    return this.http
      .get<Orders>(endpoint, {headers: this.getHeaders(), params: httpParams})
      .pipe(catchError(e => this.handleError(e)));
  }

  // get orders list, ignore active/deleted
  getOrdersIgnoreActiveDeleted(top: number = 10000, skip: number = 0): Observable<Orders> {
    const endpoint = this.getOrderUrl();
    let httpParams = new HttpParams();
    if (top) {
      httpParams = httpParams.set('$top', top.toString());
    }
    if (skip) {
      httpParams = httpParams.set('$skip', skip.toString());
    }
    httpParams = httpParams.set('active', 'null');
    httpParams = httpParams.set('deleted', 'null');
    return this.http
      .get<Orders>(endpoint, {headers: this.getHeaders(), params: httpParams})
      .pipe(catchError(e => this.handleError(e)));
  }

  // search orders
  searchOrders(top: number = 10000,
               skip: number = 0,
               id: number = null,
               uid: string = null,
               contract: string = null,
               description1: string = null,
               description2: string = null,
               acceptance: string = null,
               warranty: string = null,
               date: string = null,
               isActive: boolean = null,
               cid: number = null,
               cuid: string = null,
               crid: number = null,
               cruid: string = null,
               pid: number = null,
               puid: string = null): Observable<Orders> {
    const endpoint = this.getOrderUrl() + '/search';
    let httpParams = new HttpParams();
    if (top) {
      httpParams = httpParams.set('$top', top.toString());
    }
    if (skip) {
      httpParams = httpParams.set('$skip', skip.toString());
    }
    if (id) {
      httpParams = httpParams.set('id', id.toString());
    }
    if (uid) {
      httpParams = httpParams.set('uid', uid);
    }
    if (contract) {
      httpParams = httpParams.set('contract', contract);
    }
    if (description1) {
      httpParams = httpParams.set('description1', description1);
    }
    if (description2) {
      httpParams = httpParams.set('description2', description2);
    }
    if (acceptance) {
      httpParams = httpParams.set('acceptance', acceptance);
    }
    if (warranty) {
      httpParams = httpParams.set('warranty', warranty);
    }
    if (date) {
      httpParams = httpParams.set('date', date);
    }
    if (isActive) {
      httpParams = httpParams.set('isActive', isActive ? 'true' : 'false');
    }
    if (cid) {
      httpParams = httpParams.set('cid', cid.toString());
    }
    if (cuid) {
      httpParams = httpParams.set('cuid', cuid);
    }
    if (crid) {
      httpParams = httpParams.set('crid', crid.toString());
    }
    if (cruid) {
      httpParams = httpParams.set('cruid', cruid);
    }
    if (pid) {
      httpParams = httpParams.set('pid', pid.toString());
    }
    if (puid) {
      httpParams = httpParams.set('puid', puid);
    }
    return this.http
      .get<Orders>(endpoint, {headers: this.getHeaders(), params: httpParams})
      .pipe(catchError(e => this.handleError(e)));
  }

  // get order by id
  getOrderById(orderId: number): Observable<Order> {
    const endpoint = this.getOrderUrl() + '/' + orderId;
    return this.http
      .get<Order>(endpoint, {headers: this.getHeaders()})
      .pipe(catchError(e => this.handleError(e)));
  }

  // get order by uid
  getOrderByUid(orderUid: string): Observable<Order> {
    const endpoint = this.getOrderUrl();
    let httpParams = new HttpParams();
    httpParams = httpParams.set('uid', orderUid);
    return this.http
      .get<Order>(endpoint, {headers: this.getHeaders(), params: httpParams})
      .pipe(catchError(e => this.handleError(e)));
  }

  // create new order
  createOrder(order: Order): Observable<Order> {
    const endpoint = this.getOrderUrl();
    return this.http
      .post<Order>(endpoint, JSON.stringify(order), {headers: this.getHeaders()})
      .pipe(catchError(e => this.handleError(e)));
  }

  // update existing order
  updateOrder(orderId: number, order: Order): Observable<Order> {
    const endpoint = this.getOrderUrl() + '/' + orderId;
    return this.http
      .put<Order>(endpoint, JSON.stringify(order), {headers: this.getHeaders()})
      .pipe(catchError(e => this.handleError(e)));
  }

  // get orders list by project
  getProjectOrders(projectId: number, top: number = 10000, skip: number = 0): Observable<Orders> {
    const endpoint = this.getProjectOrderUrl(projectId);
    let httpParams = new HttpParams();
    httpParams = httpParams.set('$details', '10');
    if (top) {
      httpParams = httpParams.set('$top', top.toString());
    }
    if (skip) {
      httpParams = httpParams.set('$skip', skip.toString());
    }
    return this.http
      .get<Orders>(endpoint, {headers: this.getHeaders(), params: httpParams})
      .pipe(catchError(e => this.handleError(e)));
  }

  // get order by id and by project
  getProjectOrderById(projectId: number, orderId: number): Observable<Order> {
    const endpoint = this.getProjectOrderUrl(projectId) + '/' + orderId;
    return this.http
      .get<Order>(endpoint, {headers: this.getHeaders()})
      .pipe(catchError(e => this.handleError(e)));
  }

  // get order by uid and by project
  getProjectOrderByUid(projectId: number, orderUid: string): Observable<Order> {
    const endpoint = this.getProjectOrderUrl(projectId);
    let httpParams = new HttpParams();
    httpParams = httpParams.set('uid', orderUid);
    return this.http
      .get<Order>(endpoint, {headers: this.getHeaders(), params: httpParams})
      .pipe(catchError(e => this.handleError(e)));
  }

  // create new order to project
  createProjectOrder(projectId: number, order: Order): Observable<Order> {
    const endpoint = this.getProjectOrderUrl(projectId);
    return this.http
      .post<Order>(endpoint, JSON.stringify(order), {headers: this.getHeaders()})
      .pipe(catchError(e => this.handleError(e)));
  }

  activateOrderInProject(projectId: number, orderId: number): Observable<Order> {
    const endpoint = this.getProjectOrderConfigurationUrl(projectId) + '/' + orderId + '/activate';
    return this.http
      .put(endpoint, null, {headers: this.getHeaders()})
      .pipe(catchError(e => this.handleError(e)));
  }

  deactivateOrderInProject(projectId: number, orderId: number): Observable<Order> {
    const endpoint = this.getProjectOrderUrl(projectId) + '/' + orderId;
    return this.http
      .delete(endpoint, {headers: this.getHeaders()})
      .pipe(catchError(e => this.handleError(e)));
  }

  deleteOrders(projectId: number, orderIds: number | number[]): Observable<Partial<{ Deleted: number[] }>> {
    const endpoint = super.getFullUrl(this.projectUrl + projectId + '/configuration/orders');
    const ids = Array.isArray(orderIds) ? orderIds : [orderIds];

    return this.http
      .delete(endpoint, {
        headers: this.getHeaders(),
        body: ids
      })
      .pipe(catchError(e => this.handleError(e)));
  }
}
